import { useEffect, useRef, useState } from "react"

interface Node {
  id: string
  label: string
  x: number
  y: number
  radius: number
  color: string
  velocity: { x: number; y: number }
  originalX: number
  originalY: number
}

interface Edge {
  source: string
  target: string
  strength: number
}

interface KnowledgeGraphProps {
  lazyLoad?: boolean
}

export function KnowledgeGraph({ lazyLoad = false }: KnowledgeGraphProps) {
  const svgRef = useRef<SVGSVGElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState({ width: 600, height: 400 })
  const [isVisible, setIsVisible] = useState(!lazyLoad)
  const [nodes, setNodes] = useState<Node[]>([])
  const [edges, setEdges] = useState<Edge[]>([])
  const animationRef = useRef<number>()
  const [isHovering, setIsHovering] = useState(false)
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    if (lazyLoad) {
      const timer = setTimeout(() => {
        setIsVisible(true)
      }, 1000)

      return () => clearTimeout(timer)
    }
  }, [lazyLoad])

  useEffect(() => {
    if (lazyLoad && !isVisible) {
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true)
          if (observerRef.current) {
            observerRef.current.disconnect()
          }
        }
      }, { rootMargin: '200px', threshold: 0.1 })

      if (containerRef.current && observerRef.current) {
        observerRef.current.observe(containerRef.current)
      }
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [lazyLoad, isVisible])

  useEffect(() => {
    if (!isVisible) return

    const initialNodes: Node[] = [
      { id: "semantic-seo", label: "Semantic SEO", x: dimensions.width * 0.5, y: dimensions.height * 0.5, radius: 38, color: "#8b5cf6", velocity: { x: 0, y: 0 }, originalX: dimensions.width * 0.5, originalY: dimensions.height * 0.5, },
      { id: "topical-authority", label: "Topical Authority", x: dimensions.width * 0.3, y: dimensions.height * 0.3, radius: 32, color: "#ec4899", velocity: { x: 0, y: 0 }, originalX: dimensions.width * 0.3, originalY: dimensions.height * 0.3, },
      { id: "content-vectors", label: "Content Vectors", x: dimensions.width * 0.7, y: dimensions.height * 0.3, radius: 32, color: "#3b82f6", velocity: { x: 0, y: 0 }, originalX: dimensions.width * 0.7, originalY: dimensions.height * 0.3, },
      { id: "knowledge-graph", label: "Knowledge Graph", x: dimensions.width * 0.7, y: dimensions.height * 0.7, radius: 32, color: "#10b981", velocity: { x: 0, y: 0 }, originalX: dimensions.width * 0.7, originalY: dimensions.height * 0.7, },
      { id: "entity-relationships", label: "Entity Relationships", x: dimensions.width * 0.3, y: dimensions.height * 0.7, radius: 32, color: "#f59e0b", velocity: { x: 0, y: 0 }, originalX: dimensions.width * 0.3, originalY: dimensions.height * 0.7, },
      { id: "search-intent", label: "Search Intent", x: dimensions.width * 0.2, y: dimensions.height * 0.5, radius: 28, color: "#ef4444", velocity: { x: 0, y: 0 }, originalX: dimensions.width * 0.2, originalY: dimensions.height * 0.5, },
      { id: "nlp", label: "NLP", x: dimensions.width * 0.8, y: dimensions.height * 0.5, radius: 28, color: "#06b6d4", velocity: { x: 0, y: 0 }, originalX: dimensions.width * 0.8, originalY: dimensions.height * 0.5, },
    ]

    const initialEdges: Edge[] = [
      { source: "semantic-seo", target: "topical-authority", strength: 0.8 },
      { source: "semantic-seo", target: "content-vectors", strength: 0.7 },
      { source: "semantic-seo", target: "knowledge-graph", strength: 0.9 },
      { source: "semantic-seo", target: "entity-relationships", strength: 0.6 },
      { source: "semantic-seo", target: "search-intent", strength: 0.5 },
      { source: "semantic-seo", target: "nlp", strength: 0.7 },
      { source: "topical-authority", target: "entity-relationships", strength: 0.6 },
      { source: "content-vectors", target: "nlp", strength: 0.8 },
      { source: "knowledge-graph", target: "entity-relationships", strength: 0.9 },
      { source: "knowledge-graph", target: "nlp", strength: 0.5 },
      { source: "search-intent", target: "topical-authority", strength: 0.7 },
    ]

    setNodes(initialNodes)
    setEdges(initialEdges)
  }, [dimensions, isVisible])

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const parent = containerRef.current
        setDimensions({
          width: parent.clientWidth || 600,
          height: Math.min(500, window.innerHeight * 0.6),
        })
      }
    }

    handleResize()
    window.addEventListener("resize", handleResize)
    const initTimer = setTimeout(handleResize, 100)

    return () => {
      window.removeEventListener("resize", handleResize)
      clearTimeout(initTimer)
    }
  }, [isVisible])

  useEffect(() => {
    if (!isVisible || !nodes.length) return

    const animate = () => {
      setNodes(prevNodes => {
        return prevNodes.map(node => {
          if (isHovering) {
            const angle = Math.random() * Math.PI * 2
            const magnitude = Math.random() * 0.5
            const dx = Math.cos(angle) * magnitude
            const dy = Math.sin(angle) * magnitude

            // Calculate distance for debugging if needed
            // const distanceFromOrigin = Math.sqrt(
            //   Math.pow(node.x - node.originalX, 2) +
            //   Math.pow(node.y - node.originalY, 2)
            // )
            const springForce = 0.02
            const springX = (node.originalX - node.x) * springForce
            const springY = (node.originalY - node.y) * springForce

            return {
              ...node,
              x: node.x + dx + springX,
              y: node.y + dy + springY,
            }
          } else {
            const dx = (node.originalX - node.x) * 0.05
            const dy = (node.originalY - node.y) * 0.05
            return {
              ...node,
              x: node.x + dx,
              y: node.y + dy,
            }
          }
        })
      })

      animationRef.current = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [isHovering, isVisible, nodes.length])

  return (
    <div ref={containerRef} className="w-full h-full relative overflow-hidden">
      {!isVisible ? (
        <div className="w-full h-full flex items-center justify-center rounded-xl border-2 border-primary/30 dark:bg-gray-900/50 bg-gray-50/50">
          <div className="w-32 h-32 rounded-full bg-primary/10 animate-pulse"></div>
        </div>
      ) : (
        <div
          className={`w-full h-full overflow-hidden rounded-xl border-2 ${
            isHovering ? 'border-primary' : 'border-primary/70'
          } dark:bg-[#0A0F1E] bg-gray-50/95 transition-all duration-700`}
          role="img"
          aria-label="Interactive knowledge graph showing relationships between Semantic SEO concepts"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          style={{
            boxShadow: isHovering 
              ? '0 10px 30px rgba(124, 58, 237, 0.3)' 
              : '0 5px 15px rgba(124, 58, 237, 0.2)',
          }}
        >
          <svg
            ref={svgRef}
            width={dimensions.width}
            height={dimensions.height}
            className="w-full h-full"
            viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
          >
            <defs>
              <pattern 
                id="blueprint-grid" 
                width="50" 
                height="50" 
                patternUnits="userSpaceOnUse"
              >
                <path 
                  d="M 50 0 L 0 0 0 50" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="0.5" 
                  className="stroke-purple-500/10" 
                />
                <path 
                  d="M 25 0 L 25 50 M 0 25 L 50 25" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="0.25" 
                  className="stroke-purple-500/5" 
                />
                <circle 
                  cx="25" 
                  cy="25" 
                  r="0.5" 
                  className="fill-purple-500/10" 
                />
              </pattern>

              {nodes.map(node => (
                <radialGradient
                  key={`${node.id}-gradient`}
                  id={`${node.id}-gradient`}
                  cx="30%"
                  cy="30%"
                  r="70%"
                  fx="30%"
                  fy="30%"
                >
                  <stop offset="0%" stopColor={`${node.color}33`} />
                  <stop offset="70%" stopColor={node.color} />
                  <stop offset="100%" stopColor={`${node.color}cc`} />
                </radialGradient>
              ))}

              <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feFlood floodColor="rgba(124, 58, 237, 0.3)" result="color" />
                <feComposite in="color" in2="blur" operator="in" result="glow" />
                <feMerge>
                  <feMergeNode in="glow" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            <rect 
              width="100%" 
              height="100%" 
              fill="url(#blueprint-grid)" 
              className="dark:opacity-30 opacity-10" 
            />

            {edges.map(edge => {
              const source = nodes.find(n => n.id === edge.source)
              const target = nodes.find(n => n.id === edge.target)
              if (!source || !target) return null

              return (
                <g key={`${edge.source}-${edge.target}`}>
                  <line
                    x1={source.x}
                    y1={source.y}
                    x2={target.x}
                    y2={target.y}
                    stroke={source.color}
                    strokeWidth={1.5}
                    strokeOpacity={0.3}
                    strokeDasharray="4,4"
                    className="transition-all duration-300"
                  />
                  {isHovering && (
                    <circle 
                      r="3" 
                      fill={source.color}
                      className="animate-pulse"
                    >
                      <animateMotion
                        dur="3s"
                        repeatCount="indefinite"
                        path={`M${source.x},${source.y} L${target.x},${target.y}`}
                      />
                    </circle>
                  )}
                </g>
              )
            })}

            {nodes.map(node => (
              <g 
                key={node.id} 
                transform={`translate(${node.x},${node.y})`}
                className="transition-transform duration-300 ease-out"
              >
                <circle
                  r={node.radius + 4}
                  fill={`url(#${node.id}-gradient)`}
                  className="opacity-20"
                  filter="url(#glow)"
                />
                <circle
                  r={node.radius}
                  fill={`url(#${node.id}-gradient)`}
                  className="transition-all duration-300"
                  style={{
                    filter: isHovering ? 'url(#glow)' : 'none',
                  }}
                />
                <text
                  textAnchor="middle"
                  dy=".3em"
                  fill="white"
                  fontSize={node.radius * 0.35}
                  fontWeight="bold"
                  className="pointer-events-none select-none"
                  style={{ 
                    textShadow: '0 2px 4px rgba(0,0,0,0.4)',
                    letterSpacing: '0.5px',
                  }}
                >
                  {node.label}
                </text>
              </g>
            ))}

            <g className="pointer-events-none">
              <circle cx="20" cy="20" r="4" className="fill-primary/20" />
              <circle 
                cx={dimensions.width - 20} 
                cy="20" 
                r="4" 
                className="fill-primary/20" 
              />
              <circle 
                cx="20" 
                cy={dimensions.height - 20} 
                r="4" 
                className="fill-primary/20" 
              />
              <circle 
                cx={dimensions.width - 20} 
                cy={dimensions.height - 20} 
                r="4" 
                className="fill-primary/20" 
              />
            </g>

            <text
              x="50%"
              y={dimensions.height - 20}
              textAnchor="middle"
              className="fill-gray-400 text-sm font-medium"
              opacity={isHovering ? 0 : 1}
            >
              Hover to animate the knowledge graph
            </text>
          </svg>
        </div>
      )}
    </div>
  )
}